import Serializable from "@/core/misc/mixins/Serializable";
import PaymentTermEvent from "@/domains/PaymentTerms/PaymentTermEvent";
import Utils from "@/core/utils/Utils";
import DateSimple from "@/core/types/DateSimple";

class PaymentTerm extends Serializable {
    private _event: PaymentTermEvent | null;
    /** Через/за сколько дней до/после event будет оплата */
    public days: number | null;
    /** Процент от суммы, который будет оплачен */
    public percentage: number;

    constructor(event?: PaymentTermEvent | null, days?: number | null, percentage?: number) {
        super();
        this._event = event ?? null;
        this.days = days ?? null;
        this.percentage = percentage ?? 100;
    }

    static fromServer(data: any, result = new PaymentTerm()): PaymentTerm {
        super.fromServer(data, result);

        if (Utils.isObject(data.event)) {
            result._event = PaymentTermEvent.fromServer(data.event);
        }

        return result;
    }

    get event(): PaymentTermEvent | null {
        return this._event;
    }

    set event(value: PaymentTermEvent | null) {
        this._event = value;

        if (this._event?.id === 'before_shipment') {
            this.days = 5;
        }
    }

    /** Текстовое представление условия оплаты */
    get stringRepresentation(): string {
        if (!this.event || this.days === null) {
            return '';
        }

        const daysWord = Utils.regional.ru.pluralize(this.days, 'день', 'дня', 'дней');
        return `${this.days} ${daysWord} ${this.event.name}`;
    }

    /** Текстовое представление типа оплаты */
    get paymentTypeStringRepresentation(): string | null {
        if (!this.event?.paymentType) {
            return null;
        }
        return this.event.isPrepayment ? 'Предоплата' : 'Постоплата';
    }

    /**
     * Валидация количества дней
     * @returns null если валидно, иначе текст ошибки
     */
    validateDays(): string | null {
        if (this.days === null) {
            return 'Укажите количество дней';
        }

        switch (this.event?.id) {
            case 'before_shipment':
                // Для предоплаты до отгрузки — фиксированно 5 дней
                return null;

            case 'after_order_confirm':
            case 'after_receiving':
            case 'after_shipment':
                return (20 <= this.days && this.days <= 180)
                    ? null
                    : 'Кол-во дней должно быть в диапазоне от 20 до 180 дней';

            default:
                return null;
        }
    }

    static isPaymentTerm(value: any): value is PaymentTerm {
        return value instanceof PaymentTerm;
    }

    /** Валидны ли условия оплаты (нет ошибок в днях) */
    get isValid(): boolean {
        return !this.validateDays();
    }

    /**
     * Рассчитывает дату платежа относительно базовой даты
     * @param startDate - базовая дата (например, дата отгрузки)
     * @param deliveryDays - дней на доставку (для события 'after_receiving')
     */
    getRelativePaymentDate(startDate: DateSimple = new DateSimple(), deliveryDays: number = 0): DateSimple | null {
        if (!this.event || this.days === null) {
            return null;
        }

        const date = startDate.clone();

        switch (this.event.id) {
            case 'before_shipment':
                return date.subtract({ days: this.days });

            case 'after_shipment':
                return date.add({ days: this.days });

            case 'after_receiving':
                return date.add({ days: this.days + deliveryDays });

            case 'after_order_confirm':
                return date.add({ days: this.days });

            default:
                return null;
        }
    }

    /** Текстовое представление условия оплаты (без типа) */
    toString(): string {
        if (!this.event || this.days === null) {
            return '';
        }

        const days = `${this.days} ${Utils.regional.ru.pluralize(this.days, 'день', 'дня', 'дней')}`;

        return `${this.percentage}% в течение ${days} ${this.event.name}`;
    }
}

export default PaymentTerm;
